/**
 * TeamJobLogSheet: the newest lines of one run's log, opened from a row of
 * the staff Jobs page. The API reads them from the run's newest pod
 * (api/app/services/k8s_jobs.py `job_log`), only for a run the team's row
 * lists, so the sheet shows what `kubectl logs` would up to the API's line
 * limit, and says when older lines were left out.
 *
 * The log stays scrolled to its end, where a run fails or finishes, until
 * the reader scrolls up. An active run's log is read again on the list's
 * cadence until a read finds the run ended (useTeamJobLog).
 */

import { Loader2, RefreshCw } from 'lucide-react'
import { useLayoutEffect, useRef } from 'react'

import type { TeamJobLogResponse } from '@/api/generated/model'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeamJobLog } from '@/hooks/useTeamJobs'

/** One run, as its row names it: the run whose log is open, or the one a stop asks about. */
export interface RunTarget {
  jobSlug: string
  displayName: string
  jobName: string
}

interface TeamJobLogSheetProps {
  teamId: string
  /** Kept after the sheet closes, so the sheet does not blank while it slides out. */
  target: RunTarget | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TeamJobLogSheet({
  teamId,
  target,
  open,
  onOpenChange,
}: TeamJobLogSheetProps): React.JSX.Element {
  const read = useTeamJobLog(teamId, target?.jobSlug ?? '', open && target !== null ? target.jobName : null)
  const podName = read.log?.pod_name ?? null
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{target === null ? 'Log' : `${target.displayName} log`}</SheetTitle>
          <SheetDescription>See the newest lines this run printed.</SheetDescription>
        </SheetHeader>
        {target !== null && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 space-y-0.5 text-xs text-muted-foreground">
              <p className="break-all font-mono">{target.jobName}</p>
              {podName !== null && (
                <p className="break-all">
                  Pod <span className="font-mono">{podName}</span>
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" disabled={read.isFetching} onClick={read.refresh}>
              {read.isFetching ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw aria-hidden="true" />
              )}
              Refresh
            </Button>
          </div>
        )}
        {read.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : read.error !== null ? (
          <p className="text-sm text-destructive" role="alert">
            {read.error}
          </p>
        ) : read.log === undefined ? null : (
          <LogBody log={read.log} />
        )}
      </SheetContent>
    </Sheet>
  )
}

/** The lines, or the reason there are none. */
export function LogBody({ log }: { log: TeamJobLogResponse }): React.JSX.Element {
  if (log.unavailable_reason !== null) {
    return <p className="text-sm text-muted-foreground">{log.unavailable_reason}</p>
  }
  return (
    <>
      {log.truncated && (
        <p className="text-xs text-muted-foreground">
          Only the last {log.line_limit.toLocaleString('en-US')} lines are shown.
        </p>
      )}
      <LogText text={log.text} />
    </>
  )
}

/** How near the end a reader counts as at it, so a new read keeps them there. */
const END_SLACK_PX = 24

function LogText({ text }: { text: string }): React.JSX.Element {
  const ref = useRef<HTMLPreElement>(null)
  const isAtEnd = useRef(true)
  useLayoutEffect(() => {
    const pre = ref.current
    if (pre !== null && isAtEnd.current) pre.scrollTop = pre.scrollHeight
  }, [text])
  return (
    <pre
      ref={ref}
      tabIndex={0}
      aria-label="Run log"
      onScroll={(event) => {
        const pre = event.currentTarget
        isAtEnd.current = pre.scrollHeight - pre.scrollTop - pre.clientHeight < END_SLACK_PX
      }}
      className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-border bg-background p-3 font-mono text-xs leading-relaxed text-foreground"
    >
      {text}
    </pre>
  )
}
